import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Users, Sparkles, CalendarCheck, MapPin, HeartHandshake, Star, Facebook, Quote } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal, MaskedLines, Eyebrow, Chapter } from "../components/Reveal";
import { BeforeAfterSlider } from "../components/BeforeAfterSlider";
import { ServiceMarquee } from "../components/ServiceMarquee";
import { FaqSection, faqSchema } from "../components/FaqSection";
import { FinalCta } from "../components/FinalCta";
import { BUSINESS, SERVICES, SUBURBS, SUBURB_PAGES, WORK_ITEMS, WHY_CHOOSE, REVIEWS } from "../content";
import { track } from "../lib/track";

const ICONS = { ShieldCheck, Users, Sparkles, CalendarCheck, MapPin, HeartHandshake };

const Hero = () => {
  const { scrollY } = useScroll();
  const yMain = useTransform(scrollY, [0, 700], [0, -70]);
  const yCard = useTransform(scrollY, [0, 700], [0, -30]);

  return (
    <section data-testid="hero-section" className="relative overflow-hidden bg-brand-mist pt-32 lg:pt-40">
      <div className="pointer-events-none absolute -left-40 top-24 h-[28rem] w-[28rem] rounded-full border-[48px] border-teal-100/50" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-6 pb-24 lg:px-10 lg:pb-32">
        <div className="grid items-center gap-14 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <Eyebrow>Servicing {BUSINESS.region}</Eyebrow>
            </Reveal>
            <MaskedLines
              onLoad
              lines={["Reliable House", "Cleaning Across", "Melbourne's South East"]}
              className="font-display text-[2.6rem] font-black leading-[1.04] text-brand-ink sm:text-6xl lg:text-[4.2rem]"
            />
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.55, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-7 max-w-lg text-base leading-relaxed text-slate-600 md:text-lg"
            >
              Spend less time cleaning and more time doing what matters. Pure Bliss Clean provides reliable, detailed
              cleaning for homes, rental properties and businesses across {BUSINESS.region}.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/contact"
                data-testid="hero-quote-button"
                onClick={() => track("cta_click", { label: "hero_quote" })}
                className="group inline-flex items-center gap-2.5 rounded-full bg-brand-teal px-8 py-4 font-display text-base font-bold text-white shadow-xl shadow-teal-900/20 transition-[background-color,transform] duration-200 hover:-translate-y-1 hover:bg-teal-600"
              >
                Get a Free Quote
                <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
              <a
                href="#services"
                data-testid="hero-services-button"
                className="inline-flex items-center rounded-full border border-slate-300 bg-white px-8 py-4 font-display text-base font-bold text-brand-ink transition-colors duration-200 hover:border-brand-teal hover:text-brand-teal"
              >
                View Our Services
              </a>
            </motion.div>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.95, duration: 0.8 }}
              className="mt-8 flex items-center gap-2.5 text-sm font-semibold text-slate-500"
            >
              <Users className="h-4 w-4 text-brand-teal" />
              Familiar faces, consistent service.
            </motion.p>
          </div>

          <div className="relative lg:col-span-6">
            <motion.div
              style={{ y: yMain }}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.35, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative z-10 ml-auto aspect-[4/5] w-full max-w-md overflow-hidden rounded-[2.5rem] shadow-2xl shadow-slate-900/10 lg:mr-6"
            >
              <img src="/assets/work/vanity-after.webp" alt="Polished double vanity after a Pure Bliss Clean" className="h-full w-full object-cover" />
            </motion.div>
            <motion.div
              style={{ y: yCard }}
              initial={{ opacity: 0, y: 40, rotate: -6 }}
              animate={{ opacity: 1, y: 0, rotate: -4 }}
              transition={{ delay: 0.6, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="absolute -bottom-10 left-0 z-20 w-44 overflow-hidden rounded-3xl border-4 border-white shadow-xl sm:w-56"
            >
              <img src="/assets/work/cooktop-after.webp" alt="Spotless cooktop after a Pure Bliss Clean" className="aspect-[4/5] w-full object-cover" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.7 }}
              className="absolute -top-5 right-0 z-20 rounded-full bg-brand-deep px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-teal-100 shadow-lg"
            >
              Genuine Pure Bliss work
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

const ResidentialSection = () => (
  <section id="services" data-testid="residential-section" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
    <div className="grid items-center gap-14 lg:grid-cols-2">
      <div>
        <Reveal><Chapter number="01" label="Regular Residential Cleaning" /></Reveal>
        <MaskedLines
          as="h2"
          lines={["More Time For You.", "Less Time Cleaning."]}
          className="font-display text-3xl font-black leading-[1.08] text-brand-ink sm:text-4xl lg:text-5xl"
        />
        <Reveal delay={0.15}>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">
            Keeping your home clean shouldn't take up your entire week. Pure Bliss Clean provides reliable regular
            house cleaning designed to keep your home fresh, comfortable and cared for — without the hassle.
          </p>
          <p className="mt-4 max-w-lg text-base leading-relaxed text-slate-600">
            We aim to keep the same cleaners with our regular clients wherever possible, helping us understand your
            home, your preferences and exactly how you like things done. As local house cleaners, we're already
            looking after homes across Carrum Downs, Frankston, Cranbourne and surrounding suburbs.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <ul className="mt-8 flex flex-wrap gap-3">
            {["Weekly Cleaning", "Fortnightly Cleaning", "Monthly Cleaning", "One-Off Cleans", "Deep Cleaning"].map((f) => (
              <li key={f} className="rounded-full border border-teal-200 bg-teal-50 px-4 py-2 text-sm font-bold text-brand-teal">
                {f}
              </li>
            ))}
          </ul>
          <div className="mt-9 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="residential-quote-button"
              onClick={() => track("cta_click", { label: "residential_quote" })}
              className="inline-flex items-center gap-2 rounded-full bg-brand-teal px-7 py-3.5 font-display text-sm font-bold text-white shadow-lg shadow-teal-900/15 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-teal-600"
            >
              Get a Free Quote <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/residential-cleaning" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-display text-sm font-bold text-brand-ink underline-offset-4 transition-colors hover:text-brand-teal hover:underline">
              Explore residential cleaning
            </Link>
          </div>
        </Reveal>
      </div>
      <Reveal delay={0.2} className="relative">
        <div className="overflow-hidden rounded-[2.5rem] shadow-2xl shadow-slate-900/10">
          <img src="/assets/work/bathtoys-after.webp" alt="Fresh, reset family bathroom after a Pure Bliss Clean" loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
        </div>
        <div className="absolute -bottom-6 -left-4 max-w-[16rem] rounded-3xl border border-slate-200 bg-white p-6 shadow-xl sm:-left-8">
          <Quote className="h-6 w-6 text-brand-teal" />
          <p className="mt-3 font-display text-sm font-bold leading-snug text-brand-ink">
            We focus on the small details that make your home genuinely feel clean.
          </p>
        </div>
      </Reveal>
    </div>
  </section>
);

const RealResults = () => {
  const featured = WORK_ITEMS.filter((w) => ["cooktop", "bath"].includes(w.id));
  return (
    <section data-testid="real-results-section" className="bg-white">
      <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
        <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
          <div className="lg:col-span-7">
            <Reveal><Eyebrow>Our Work</Eyebrow></Reveal>
            <MaskedLines
              as="h2"
              lines={["Real Homes.", "Real Results."]}
              className="font-display text-3xl font-black leading-[1.08] text-brand-ink sm:text-4xl lg:text-5xl"
            />
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="max-w-md text-base leading-relaxed text-slate-600 lg:ml-auto">
              See the difference a detailed Pure Bliss Clean can make. These are genuine results from homes we've
              cleaned across {BUSINESS.region} — drag the slider to compare.
            </p>
          </Reveal>
        </div>
        <div className="mt-14 grid gap-10 sm:grid-cols-2 lg:gap-14">
          {featured.map((w, i) => (
            <Reveal key={w.id} delay={0.12 * i}>
              <BeforeAfterSlider {...w} />
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2} className="mt-12 flex flex-wrap items-center justify-between gap-6">
          <p className="flex items-center gap-2.5 text-sm font-semibold text-slate-500">
            <Sparkles className="h-4 w-4 text-brand-teal" />
            Genuine Pure Bliss Clean work — no stock before &amp; after photos.
          </p>
          <Link
            to="/our-work"
            data-testid="see-more-work-button"
            className="group inline-flex items-center gap-2 rounded-full border border-slate-300 px-7 py-3.5 font-display text-sm font-bold text-brand-ink transition-colors duration-200 hover:border-brand-teal hover:text-brand-teal"
          >
            See More of Our Work
            <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </Reveal>
      </div>
    </section>
  );
};

const WhyChoose = () => (
  <section data-testid="why-choose-section" className="bg-brand-mist">
    <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <Reveal className="max-w-2xl">
        <Eyebrow>Why Pure Bliss</Eyebrow>
        <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl lg:text-5xl">Why Choose Pure Bliss Clean?</h2>
      </Reveal>
      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {WHY_CHOOSE.map((b, i) => {
          const Icon = ICONS[b.icon];
          return (
            <Reveal key={b.title} delay={0.08 * i} className={i === 0 || i === 5 ? "lg:col-span-2" : ""}>
              <div
                data-testid={`benefit-card-${i}`}
                className="group h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-sm transition-[box-shadow,transform] duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-teal-900/5"
              >
                <div className="inline-flex rounded-2xl bg-teal-50 p-3.5 text-brand-teal transition-colors duration-300 group-hover:bg-brand-teal group-hover:text-white">
                  <Icon className="h-6 w-6" />
                </div>
                <h3 className="mt-5 font-display text-xl font-bold text-brand-ink">{b.title}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{b.text}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const PropertyManagersSection = () => (
  <section data-testid="property-managers-section" className="bg-brand-deep">
    <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <Reveal><Chapter number="02" label="Property Managers & Real Estates" light /></Reveal>
          <MaskedLines
            as="h2"
            lines={["Reliable Cleaning For", "Property Managers", "& Real Estates"]}
            className="font-display text-3xl font-black leading-[1.08] text-white sm:text-4xl lg:text-5xl"
          />
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-teal-100/85">
              Managing properties means relying on contractors who communicate clearly, turn up when expected and
              complete the job properly. Pure Bliss Clean provides dependable cleaning support for rental properties,
              vacates, inspections and ongoing property requirements.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <ul className="mt-8 grid max-w-lg grid-cols-1 gap-x-6 gap-y-3 text-sm font-semibold text-teal-100 sm:grid-cols-2">
              {["Routine property cleaning", "End-of-lease cleaning", "Vacant property cleaning", "Pre-inspection cleaning", "Pre-sale cleaning", "Ongoing arrangements"].map((s) => (
                <li key={s} className="flex items-center gap-2.5">
                  <ShieldCheck className="h-4 w-4 shrink-0 text-teal-300" /> {s}
                </li>
              ))}
            </ul>
            <Link
              to="/property-managers"
              data-testid="property-managers-cta"
              onClick={() => track("cta_click", { label: "property_managers" })}
              className="group mt-10 inline-flex items-center gap-2.5 rounded-full bg-white px-8 py-4 font-display text-base font-bold text-brand-deep shadow-xl transition-transform duration-200 hover:-translate-y-1"
            >
              Talk To Us About Your Properties
              <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
            <p className="mt-6 text-sm text-teal-100/60">
              We already provide ongoing scheduled cleaning services across residential, real estate and care environments.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="grid grid-cols-2 gap-5">
            <div className="overflow-hidden rounded-[2rem] shadow-2xl">
              <img src="/assets/work/bath-after.webp" alt="Gleaming bath after a Pure Bliss end-of-lease standard clean" loading="lazy" className="aspect-[4/5] w-full object-cover" />
            </div>
            <div className="mt-10 overflow-hidden rounded-[2rem] shadow-2xl">
              <img src="/assets/work/cooktop-after.webp" alt="Inspection-ready cooktop after a Pure Bliss Clean" loading="lazy" className="aspect-[4/5] w-full object-cover" />
            </div>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Reviews = () => (
  <section data-testid="reviews-section" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
    <Reveal className="mx-auto max-w-2xl text-center">
      <div className="flex justify-center"><Eyebrow>Reviews</Eyebrow></div>
      <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl lg:text-5xl">What Our Customers Say</h2>
    </Reveal>
    {REVIEWS.length > 0 ? (
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {REVIEWS.map((r, i) => (
          <Reveal key={i} delay={0.1 * i}>
            <figure className="h-full rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
              <div className="flex gap-1 text-amber-400" aria-label="5 star review">
                {[...Array(5)].map((_, s) => <Star key={s} className="h-4 w-4 fill-current" />)}
              </div>
              <blockquote className="mt-4 text-sm leading-relaxed text-slate-600">"{r.text}"</blockquote>
              <figcaption className="mt-5 font-display text-sm font-bold text-brand-ink">
                {r.name} <span className="font-medium text-slate-400">· Google review</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    ) : (
      <Reveal delay={0.15}>
        <div className="mx-auto mt-12 max-w-xl rounded-3xl border border-dashed border-teal-300 bg-teal-50/60 p-10 text-center">
          <Star className="mx-auto h-8 w-8 text-brand-teal" />
          <p className="mt-4 text-sm leading-relaxed text-slate-600">
            Our genuine Google reviews are being added here shortly. In the meantime, you can see recent feedback
            from local customers on our Facebook page.
          </p>
          <a
            href={BUSINESS.facebook}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="reviews-facebook-link"
            onClick={() => track("social_click", { network: "facebook", location: "reviews" })}
            className="mt-6 inline-flex items-center gap-2 rounded-full border border-brand-teal px-6 py-3 text-sm font-bold text-brand-teal transition-colors hover:bg-brand-teal hover:text-white"
          >
            <Facebook className="h-4 w-4" /> Visit us on Facebook
          </a>
        </div>
      </Reveal>
    )}
  </section>
);

const EndOfLeaseSection = () => (
  <section data-testid="end-of-lease-section" className="bg-white">
    <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <Reveal className="order-2 lg:order-1">
          <div className="overflow-hidden rounded-[2.5rem] shadow-2xl shadow-slate-900/10">
            <img src="/assets/work/bath-before.webp" alt="A rental property bath before a detailed Pure Bliss end-of-lease clean" loading="lazy" className="aspect-[4/5] w-full object-cover transition-transform duration-700 hover:scale-105" />
          </div>
        </Reveal>
        <div className="order-1 lg:order-2">
          <Reveal><Chapter number="03" label="End of Lease Cleaning" /></Reveal>
          <MaskedLines
            as="h2"
            lines={["Moving Out?", "Leave The Cleaning To Us."]}
            className="font-display text-3xl font-black leading-[1.08] text-brand-ink sm:text-4xl lg:text-5xl"
          />
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">
              Moving is stressful enough without spending your final days scrubbing the property. Pure Bliss Clean
              provides detailed end-of-lease cleaning to help leave your home clean, presentable and ready for inspection.
            </p>
            <Link
              to="/end-of-lease-cleaning"
              data-testid="end-of-lease-cta"
              onClick={() => track("cta_click", { label: "end_of_lease" })}
              className="group mt-9 inline-flex items-center gap-2.5 rounded-full bg-brand-ink px-8 py-4 font-display text-base font-bold text-white shadow-lg transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-slate-800"
            >
              Request an End of Lease Quote
              <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </div>
    </div>
  </section>
);

const CommercialSection = () => (
  <section data-testid="commercial-section" className="bg-brand-mist">
    <div className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
      <div className="grid items-center gap-14 lg:grid-cols-2">
        <div>
          <Reveal><Chapter number="04" label="Commercial Cleaning" /></Reveal>
          <MaskedLines
            as="h2"
            lines={["Professional", "Commercial Cleaning"]}
            className="font-display text-3xl font-black leading-[1.08] text-brand-ink sm:text-4xl lg:text-5xl"
          />
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600">
              Keep your workplace clean, presentable and welcoming with reliable commercial cleaning tailored around
              your business — offices, small businesses, real estate offices, care environments and commercial facilities.
            </p>
            <Link
              to="/commercial-cleaning"
              data-testid="commercial-cta"
              className="group mt-9 inline-flex items-center gap-2.5 rounded-full bg-brand-teal px-8 py-4 font-display text-base font-bold text-white shadow-lg shadow-teal-900/15 transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-teal-600"
            >
              Request a Commercial Cleaning Quote
              <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
        <Reveal delay={0.2}>
          <div className="overflow-hidden rounded-[2.5rem] shadow-2xl shadow-slate-900/10">
            <img
              src="https://images.unsplash.com/photo-1718220216044-006f43e3a9b1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwzfHxwcmlzdGluZSUyMGNsZWFuJTIwY29tbWVyY2lhbCUyMG9mZmljZSUyMHNwYWNlfGVufDB8fHx8MTc4NzAyNTEyOHww&ixlib=rb-4.1.0&q=85"
              alt="Pristine, freshly cleaned commercial office space"
              loading="lazy"
              className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105"
            />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const ServiceAreasSection = () => (
  <section data-testid="service-areas-section" className="mx-auto max-w-7xl px-6 py-24 lg:px-10 lg:py-32">
    <div className="grid gap-12 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <Eyebrow>Service Areas</Eyebrow>
        <h2 className="font-display text-3xl font-black text-brand-ink sm:text-4xl">
          House Cleaning Services Across {BUSINESS.region}
        </h2>
        <p className="mt-5 text-base leading-relaxed text-slate-600">
          Servicing {BUSINESS.baseSuburb} and surrounding {BUSINESS.region} suburbs. Looking for a local house
          cleaner in <Link to="/house-cleaning-carrum-downs" className="font-semibold text-brand-teal underline-offset-4 hover:underline">Carrum Downs</Link>,{" "}
          <Link to="/house-cleaning-frankston" className="font-semibold text-brand-teal underline-offset-4 hover:underline">Frankston</Link> or{" "}
          <Link to="/house-cleaning-cranbourne" className="font-semibold text-brand-teal underline-offset-4 hover:underline">Cranbourne</Link>?
          Nearby but not listed? Get in touch — surrounding areas are often fine too.
        </p>
        <Link
          to="/service-areas"
          data-testid="service-areas-link"
          className="group mt-7 inline-flex items-center gap-2 font-display text-sm font-bold text-brand-teal"
        >
          View all service areas
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
        </Link>
      </Reveal>
      <div className="lg:col-span-7">
        <ul className="flex flex-wrap gap-3">
          {SUBURBS.map((s, i) => {
            const page = SUBURB_PAGES.find((p) => p.suburb === s);
            const pill = (
              <li className="rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-semibold text-brand-ink shadow-sm transition-colors duration-200 hover:border-teal-300 hover:text-brand-teal">
                {s}
              </li>
            );
            return (
              <Reveal key={s} delay={0.03 * i} y={16}>
                {page ? <Link to={`/${page.slug}`} data-testid={`home-suburb-link-${s.toLowerCase().replace(/\s+/g, "-")}`}>{pill}</Link> : pill}
              </Reveal>
            );
          })}
        </ul>
      </div>
    </div>
  </section>
);

export const HomePage = () => (
  <>
    <Seo
      title="House Cleaning Melbourne South East | Local House Cleaners | Pure Bliss Clean"
      description="Looking for a reliable house cleaner in Melbourne's South East? Pure Bliss Clean provides regular house cleaning, deep cleans and end of lease cleaning across Carrum Downs, Frankston, Cranbourne and surrounds. Get a free quote."
      path="/"
      schema={faqSchema()}
    />
    <Hero />
    <ServiceMarquee items={SERVICES.map((s) => s.name)} />
    <ResidentialSection />
    <RealResults />
    <WhyChoose />
    <PropertyManagersSection />
    <Reviews />
    <EndOfLeaseSection />
    <CommercialSection />
    <ServiceAreasSection />
    <FaqSection />
    <FinalCta />
  </>
);
